import {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "./shadcn/input";
import { Button } from "./shadcn/button";
import { cn } from "@/lib/utils";

export const PageChanger = ({
  currentPage,
  setCurrentPage,
  totalPages,
  maxWidth,
  onPrefetchNextPage,
  size = "md",
  erroredPages,
}: {
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  maxWidth: string;
  onPrefetchNextPage?: () => void;
  size?: "md" | "sm" | "xs";
  // Optional; styles the number input destructive when the current page is one
  // of these. Omitted by existing callers → no change.
  erroredPages?: number[];
}) => {
  const [inputValue, setInputValue] = useState<string>(currentPage.toString());

  const isMd = size === "md";
  const isSm = size === "sm";
  const isXs = size === "xs";

  useEffect(() => {
    setInputValue(currentPage.toString());
  }, [currentPage, setCurrentPage]);

  const handlePageDecrease = useCallback(() => {
    setCurrentPage(currentPage - 1);
    setInputValue((currentPage - 1).toString());
  }, [currentPage, setCurrentPage]);

  const handlePageIncrease = useCallback(() => {
    setCurrentPage(currentPage + 1);
    setInputValue((currentPage + 1).toString());
  }, [currentPage, setCurrentPage]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [setInputValue]
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      let number = Number(e.target.value);

      if (isNaN(number)) {
        setCurrentPage(currentPage);
        setInputValue(currentPage.toString());
      } else {
        if (number > totalPages) {
          number = totalPages;
        } else if (number < 1) {
          number = 1;
        } else if (!Number.isInteger(number)) {
          number = Math.floor(number);
        }
        setCurrentPage(number);
        setInputValue(number.toString());
      }
    },
    [currentPage, setCurrentPage, totalPages]
  );

  const handleKeydown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        let number = Number(inputValue);

        if (isNaN(number)) {
          setCurrentPage(currentPage);
          setInputValue(currentPage.toString());
        } else {
          if (number > totalPages) {
            number = totalPages;
          } else if (number < 1) {
            number = 1;
          } else if (!Number.isInteger(number)) {
            number = Math.floor(number);
          }
          setCurrentPage(number);
          setInputValue(number.toString());
        }
      }
    },
    [inputValue, currentPage, setCurrentPage, totalPages]
  );

  const buttonSize = isMd ? "h-8 w-8" : isSm ? "h-6 w-6" : "h-4 w-4";
  const iconSize = isMd ? "h-5 w-5" : isSm ? "h-4 w-4" : "h-3 w-3";
  const inputSize = isMd
    ? "w-10 h-8 text-sm leading-8"
    : isSm
      ? "w-8 h-6 text-sm leading-6"
      : "w-6 h-5 text-[10px] leading-5";
  const textSize = isMd ? "text-sm" : isSm ? "text-sm" : "text-[10px]";
  const currentIsErrored = !!erroredPages?.includes(currentPage);

  return (
    <div className="flex items-center shrink-0">
      <Button
        variant="ghost"
        aria-label={"Go to previous page"}
        isDisabled={currentPage === 1}
        onClick={handlePageDecrease}
        size="icon"
        className={`${buttonSize} mr-2`}
      >
        <ChevronLeft className={iconSize} strokeWidth={1.33} />
      </Button>
      <Input
        data-testid="page-number"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeydown}
        aria-invalid={currentIsErrored}
        className={cn(
          `${inputSize} text-center px-1 mr-2 py-0`,
          currentIsErrored && "text-destructive"
        )}
      />
      <span className={`${textSize} text-muted-foreground`}>of</span>
      <span className={`${textSize} text-foreground font-medium ml-1`}>
        {totalPages}
      </span>
      <Button
        className={`${buttonSize} ml-2`}
        variant="ghost"
        aria-label={"Go to next page"}
        isDisabled={currentPage === totalPages}
        onClick={handlePageIncrease}
        onMouseEnter={onPrefetchNextPage}
        size="icon"
      >
        <ChevronRight className={iconSize} strokeWidth={1.33} />
      </Button>
    </div>
  );
};
